import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

export const useShoppingItems = (listId) => {
  const { user } = useAuth()
  const qc  = useQueryClient()
  const key = ['shopping-items', listId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!listId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('shopping_items')
        .select('id, list_id, name, quantity, store, category, checked_by, checked_at, added_by')
        .eq('list_id', listId)
        .order('created_at')
      if (error) throw error
      return data ?? []
    },
  })

  useEffect(() => {
    if (!listId) return
    const channel = supabase
      .channel(`shopping-items-${listId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shopping_items', filter: `list_id=eq.${listId}` },
        () => qc.invalidateQueries({ queryKey: key }),
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [listId, qc])

  const invalidate = () => qc.invalidateQueries({ queryKey: key })

  const addItem = async ({ name, quantity, store, category }) => {
    const { error } = await supabase.from('shopping_items').insert({
      list_id: listId, name, quantity: quantity || null,
      store: store || null, category: category || null, added_by: user.id,
    })
    if (error) throw error
    invalidate()
  }

  const addItems = async (items) => {
    const rows = items.map((i) => ({ ...i, list_id: listId, added_by: user.id }))
    const { error } = await supabase.from('shopping_items').insert(rows)
    if (error) throw error
    invalidate()
  }

  const checkItem = async (item) => {
    const checked = !!item.checked_at
    const updates = {
      checked_at: checked ? null : new Date().toISOString(),
      checked_by: checked ? null : user.id,
    }
    // For check/uncheck conflicts, use last-write-wins silently
    if (item.updated_at) {
      const result = await saveWithConflictCheck('shopping_items', item.id, updates, item.updated_at)
      if (result.conflict) {
        // Another user already toggled — just refetch to show current state
        invalidate()
        return
      }
    } else {
      const { error } = await supabase.from('shopping_items').update(updates).eq('id', item.id)
      if (error) throw error
    }
    invalidate()
  }

  const deleteItem = async (id) => {
    const { error } = await supabase.from('shopping_items').delete().eq('id', id)
    if (error) throw error
    invalidate()
  }

  const clearChecked = async () => {
    const { error } = await supabase
      .from('shopping_items')
      .delete()
      .eq('list_id', listId)
      .not('checked_at', 'is', null)
    if (error) throw error
    invalidate()
  }

  return { ...query, addItem, addItems, checkItem, deleteItem, clearChecked }
}
